import { LocationHours } from '../types/locations';
import { getNowInTimezone, DEFAULT_TIMEZONE } from '@/lib/date-utils';

/**
 * Formats time from "08:00:00" format to "8:00 AM" format
 */
export function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours ?? '0', 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes ?? '00'} ${ampm}`;
}

export interface StoreStatus {
  isOpen: boolean;
  message: string;
}

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Converts time string "HH:MM:SS" to minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/**
 * Gets relative day label (today, tomorrow, or day name)
 */
function getRelativeDayLabel(targetDay: string, today: string): string {
  if (targetDay === today) return 'today';
  const todayIndex = DAY_ORDER.indexOf(today);
  const tomorrowIndex = (todayIndex + 1) % 7;
  if (targetDay === DAY_ORDER[tomorrowIndex]) return 'tomorrow';
  return targetDay;
}

/**
 * Determines if the store is currently open and returns appropriate status message
 * @param hours - Array of location hours
 * @param timezone - IANA timezone identifier (defaults to America/Chicago)
 */
export function getStoreStatus(
  hours: LocationHours[],
  timezone: string = DEFAULT_TIMEZONE
): StoreStatus {
  const now = getNowInTimezone(timezone);
  if (!hours || hours.length === 0) {
    return { isOpen: false, message: 'Hours not available' };
  }

  const currentDayIndex = now.getDay();
  const currentDay = DAY_ORDER[currentDayIndex] ?? 'Sunday';
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Find today's hours
  const todayHours = hours.find(h => h.DayOfOperations === currentDay);

  if (todayHours) {
    const openMinutes = timeToMinutes(todayHours.LocationOpeningHours);
    const closeMinutes = timeToMinutes(todayHours.LocationClosingHours);

    // Check if currently open
    if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
      return {
        isOpen: true,
        message: `Open today until ${formatTime(todayHours.LocationClosingHours)}`,
      };
    }

    // Store hasn't opened yet today
    if (currentMinutes < openMinutes) {
      return {
        isOpen: false,
        message: `Opens today at ${formatTime(todayHours.LocationOpeningHours)}`,
      };
    }
  }

  // Store is closed - find next opening day
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = DAY_ORDER[nextDayIndex] ?? 'Sunday';
    const nextDayHours = hours.find(h => h.DayOfOperations === nextDay);

    if (nextDayHours) {
      const relativeDay = getRelativeDayLabel(nextDay, currentDay);
      return {
        isOpen: false,
        message: `Opens ${relativeDay} at ${formatTime(nextDayHours.LocationOpeningHours)}`,
      };
    }
  }

  return { isOpen: false, message: 'Hours not available' };
}

/**
 * Groups consecutive days with the same hours and formats them compactly
 * Example: "Mon-Fri: 8:00 AM - 10:00 PM"
 */
export function formatLocationHours(hours: LocationHours[]): string {
  if (!hours || hours.length === 0) {
    return 'Hours not available';
  }

  // Group consecutive days with same hours
  const groups: Array<{ days: string[]; open: string; close: string }> = [];
  
  for (const hour of hours) {
    const open = formatTime(hour.LocationOpeningHours);
    const close = formatTime(hour.LocationClosingHours);
    const timeRange = `${open} - ${close}`;
    
    // Find existing group with same hours
    const existingGroup = groups.find(
      (g) => g.open === open && g.close === close
    );
    
    if (existingGroup) {
      existingGroup.days.push(hour.DayOfOperations);
    } else {
      groups.push({
        days: [hour.DayOfOperations],
        open,
        close,
      });
    }
  }

  // Format each group
  const formattedGroups = groups.map((group) => {
    const days = group.days;
    let dayRange: string;
    
    if (days.length === 1) {
      dayRange = days[0];
    } else if (days.length === 2) {
      dayRange = `${days[0]} & ${days[1]}`;
    } else {
      // Check if days are consecutive
      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const sortedDays = days.sort((a, b) => 
        dayOrder.indexOf(a) - dayOrder.indexOf(b)
      );
      
      // Check if consecutive
      let isConsecutive = true;
      for (let i = 1; i < sortedDays.length; i++) {
        const prevIndex = dayOrder.indexOf(sortedDays[i - 1]);
        const currIndex = dayOrder.indexOf(sortedDays[i]);
        if (currIndex !== prevIndex + 1 && !(prevIndex === 6 && currIndex === 0)) {
          isConsecutive = false;
          break;
        }
      }
      
      if (isConsecutive && sortedDays.length > 2) {
        dayRange = `${sortedDays[0]}-${sortedDays[sortedDays.length - 1]}`;
      } else {
        dayRange = days.join(', ');
      }
    }
    
    return `${dayRange}: ${group.open} - ${group.close}`;
  });

  return formattedGroups.join('\n');
}




